import { useCallback } from 'react';
import React from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useStoreContext } from 'features/store';
import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { useAppTakeoverSimpleHomePage } from 'components/app-takeover/use-app-takeover';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { RunningExperiments, useExperiments } from 'state/experiments';
import { AccessibleMainContent } from 'state/ui';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';
import { routes } from 'utils/routing';
import { isDelivery, isPickup } from 'utils/service-mode';

import { ICTAButtonProps, ICTAButtonsProps } from '../types';

const CTAButton = ({
  children,
  iconVariant,
  defaultLinkTo,
  variant,
  isMatchingServiceMode,
  numberOfItemsInCart,
  navigate,
  marginTop,
  eventName,
  itemId,
  ...rest
}: ICTAButtonProps) => {
  const { noStoreSelected } = useStoreContext();
  const shouldGoToCart = isMatchingServiceMode && numberOfItemsInCart > 0;
  const shouldGoToMenu = isMatchingServiceMode && !noStoreSelected;
  const { formatMessage } = useIntl();
  const { openSignInModal, isAuthenticated } = useAuthContext();
  const isSignUpAfterCartEnabled = useIsSignUpAfterCartEnabled();
  const { logExposure } = useExperiments();

  const handleOnPress = useCallback(() => {
    let path: string = defaultLinkTo;
    path = shouldGoToMenu ? '/menu' : path;
    path = shouldGoToCart ? '/cart' : path;

    if (shouldGoToCart) {
      hapticNotification({ type: HapticsNotificationType.SUCCESS });
    }

    if (shouldGoToCart && !isAuthenticated) {
      logExposure(RunningExperiments.SignInUpAfterCart);

      if (!isSignUpAfterCartEnabled) {
        openSignInModal({
          navigateOnSuccessRoute: routes.cart,
        });

        return;
      }
    }

    logEvent(eventName, EventTypes.Navigation, {
      'Hero URL': path,
      'Hero ID': itemId,
    });

    return navigate(path);
  }, [
    isAuthenticated,
    defaultLinkTo,
    eventName,
    itemId,
    navigate,
    shouldGoToMenu,
    shouldGoToCart,
    isSignUpAfterCartEnabled,
    openSignInModal,
    logExposure,
  ]);

  const continueToCartText = shouldGoToCart
    ? formatMessage({ id: 'continueToCart' }, { numberOfItemsInCart })
    : children;

  return (
    <ActionButton
      minHeight={50}
      variant={variant}
      eventAttributes={{ Name: shouldGoToCart ? continueToCartText : children }}
      leftIcon={<Icon variant={iconVariant} />}
      onPress={handleOnPress}
      marginTop={marginTop}
      accessibilityLabel={children}
      {...rest}
    >
      {continueToCartText}
    </ActionButton>
  );
};

export const CTAButtons = ({
  numberOfItemsInCart,
  serviceMode,
  itemId,
  navigate,
}: ICTAButtonsProps) => {
  const { isTakeoverActive, deliveryButton } = useAppTakeoverSimpleHomePage(itemId);
  const { formatMessage } = useIntl();

  return (
    <AccessibleMainContent>
      <CTAButton
        variant={
          isTakeoverActive ? ActionButtonVariants.PRIMARY_TAKEOVER : ActionButtonVariants.PRIMARY
        }
        defaultLinkTo={
          serviceMode ? `${routes.storeLocator}?redirectUrl=${routes.menu}` : routes.menu
        }
        iconVariant="cart"
        isMatchingServiceMode={isPickup(serviceMode)}
        numberOfItemsInCart={numberOfItemsInCart}
        navigate={navigate}
        eventName={CustomEventNames.HERO_PRIMARY_CTA_CLICK}
        itemId={itemId}
      >
        {formatMessage({ id: 'orderPickup' })}
      </CTAButton>
      <CTAButton
        variant={ActionButtonVariants.OUTLINE}
        defaultLinkTo={routes.deliveryAddress}
        iconVariant="delivery"
        isMatchingServiceMode={isDelivery(serviceMode)}
        numberOfItemsInCart={numberOfItemsInCart}
        navigate={navigate}
        marginTop="$2"
        eventName={CustomEventNames.HERO_COMPLEMENTARY_CTA_CLICK}
        itemId={itemId}
        {...deliveryButton}
      >
        {formatMessage({ id: 'orderDelivery' })}
      </CTAButton>
    </AccessibleMainContent>
  );
};
